import APIKeysTable from "@/components/api-keys-table";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ApiKeyGuide } from "@/components/ui/api-key-guide";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import RemoteMCP from "@/components/ui/remote-mcp";
import { useToast } from "@/components/ui/use-toast";
import { useSubscription } from "@/hooks/use-subscription";
import { apiClient } from "@/lib/api";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/dashboard/api-keys/")({
  component: ApiKeysPage,
});

function ApiKeysPage() {
  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { subscription, loading: subscriptionLoading } = useSubscription();

  // Check if user has an active subscription
  const hasActiveSubscription =
    subscription &&
    (subscription.status === "active" || subscription.plan_type !== "free");

  useEffect(() => {
    const fetchApiKeys = async () => {
      try {
        const response = await apiClient.getApiKeys();
        setApiKeys(response.apiKeys || []);
      } catch (error) {
        console.error("Failed to fetch API keys:", error);
        toast({
          title: "Error",
          description: "Failed to load API keys",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchApiKeys();
  }, [toast]);

  if (loading || subscriptionLoading) {
    return (
      <ProtectedRoute>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">API Keys</h1>
          <p className="text-gray-600 mt-2">Manage your API keys</p>
        </div>
        {/* Subscription Notice */}
        {!hasActiveSubscription && (
          <ApiKeyGuide
            variant="card"
            title="Get Your API Key"
            description="API keys are automatically generated when you purchase a subscription plan. Choose a plan that fits your needs to start using our API."
            showSteps={true}
          />
        )}

        {/* API Keys List */}
        {loading ? (
          <Card>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : apiKeys.length === 0 ? (
          <Card>
            <CardContent>
              <ApiKeyGuide
                variant="inline"
                title={"No API keys yet"}
                description={
                  "Get your API key by purchasing a subscription plan. Your key will be automatically generated and ready to use."
                }
              />
            </CardContent>
          </Card>
        ) : (
          <div>
            <Card className="mb-4">
              <CardContent>
                <APIKeysTable apiKeys={apiKeys} />
              </CardContent>
            </Card>
            <RemoteMCP apiKey={apiKeys[0].apiKey} />
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
}
